import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { GetScormCoursesBaseUrl } from "../constants/config";
import Overlay from './Overlay';

const ScormCourseViewer = forwardRef((props, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const iframeRef = useRef(null);
  const initialLoadRef = useRef(true);
  const previousLinkRef = useRef(null);
  const sourceUpdatedRef = useRef(false);
  
  let viewerClass = "flex-1";
  if (props.hasChapters) {
    viewerClass += " position-relative";
  }

  // Expose functions to parent component via ref
  useImperativeHandle(ref, () => ({
    resetSource: () => {
      sourceUpdatedRef.current = false;
      previousLinkRef.current = null;
      setLoadError(null);
    }
  }), []);

  // Effect for handling coursePageLink changes - with sourceUpdated tracking
  useEffect(() => {
    // Allow update if coursePageLink changed OR if reset was called
    if (props.coursePageLink && props.coursePageLink !== previousLinkRef.current) {
      // Update previous link reference
      previousLinkRef.current = props.coursePageLink;
      
      // Reset the flag to allow this update
      sourceUpdatedRef.current = false;
      
      // Only show loading on initial load
      if (initialLoadRef.current) {
        setIsLoading(true);
      }
      
      const coursesBaseUrl = GetScormCoursesBaseUrl();
      const pageLink = `${coursesBaseUrl}/${props.courseRootPath}/${props.coursePageLink}`;
      console.log(`[SCORM Debug] Setting iframe src to: ${pageLink}`);
      console.log(`[SCORM Debug] Base URL: ${coursesBaseUrl}`);
      console.log(`[SCORM Debug] Root path: ${props.courseRootPath}`);
      console.log(`[SCORM Debug] Page link: ${props.coursePageLink}`);
      
      // Clear any previous errors
      setLoadError(null);
      
      // Update the iframe source
      setIframeSrc(pageLink);
    }
  }, [props.coursePageLink, props.courseRootPath]);

  // Separate effect for iframe load handling
  useEffect(() => {
    if (!iframeSrc || !iframeRef.current) return;
    
    const iframe = iframeRef.current;
    const handleLoad = () => {
      console.log("[SCORM Debug] Iframe loaded successfully!");
      setIsLoading(false);
      initialLoadRef.current = false; // Mark initial load as complete
      
      // Set the source updated flag AFTER load completes
      sourceUpdatedRef.current = true;
      
      // Try to ensure the iframe can communicate back
      try {
        // Post a ready message to the iframe content if it can receive it
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage({ type: 'parent-ready' }, '*');
        }
      } catch (error) {
        console.warn('[SCORM Debug] Could not post message to iframe:', error);
      }
    };
    
    const handleError = (error) => {
      console.error("[SCORM Debug] Iframe load error:", error);
      setIsLoading(false);
      setLoadError('Failed to load course content. Please check CORS settings on the content server.');
    };

    iframe.addEventListener('load', handleLoad);
    iframe.addEventListener('error', handleError);
    
    return () => {
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
    };
  }, [iframeSrc]);

  // Reset loading state if src is cleared
  useEffect(() => {
    if (!iframeSrc) {
      setIsLoading(false);
    }
  }, [iframeSrc]);

  // Listen for messages from the iframe content
  useEffect(() => {
    const handleMessage = (event) => {
      // Accept messages from any origin for SCORM content
      // In production, you might want to validate event.origin against known SCORM content URLs
      
      // Log all messages for debugging
      console.log('[ScormCourseViewer] Received message:', {
        origin: event.origin,
        data: event.data,
        source: event.source === iframeRef.current?.contentWindow ? 'iframe' : 'other'
      });
      
      // Handle specific message types if needed
      if (event.data && typeof event.data === 'object') {
        const { type } = event.data;
        
        // Handle navigation or other iframe events
        if (type === 'navigation' || type === 'next' || type === 'previous') {
          console.log('[ScormCourseViewer] Navigation event:', event.data);
        }
        
        // Handle SCORM API calls that might come through postMessage
        if (type === 'scorm' || type === 'xapi') {
          console.log('[ScormCourseViewer] SCORM/xAPI event:', event.data);
        }
      }
    };

    const handleClick = (event) => {
      console.log('[ScormCourseViewer] Click detected in parent:', {
        target: event.target,
        clientX: event.clientX,
        clientY: event.clientY,
        iframeContains: iframeRef.current?.contains(event.target)
      });
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('click', handleClick, true);
    
    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return (
    <div className={viewerClass} style={{ position: 'relative', pointerEvents: 'auto' }}>
      {!props.hasChapters && isLoading && <Overlay LoadingText="Loading..." />}
      {loadError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          padding: '20px',
          backgroundColor: '#fee',
          border: '2px solid #c00',
          borderRadius: '8px',
          maxWidth: '80%',
          textAlign: 'center',
          zIndex: 1000
        }}>
          <h3 style={{ color: '#c00', marginTop: 0 }}>Error Loading Content</h3>
          <p>{loadError}</p>
          <p style={{ fontSize: '12px', color: '#666' }}>Check console for details</p>
        </div>
      )}
      {iframeSrc && (
        <iframe
          key={iframeSrc}
          ref={iframeRef}
          src={iframeSrc}
          title="Course"
          allowFullScreen
          allow="autoplay; fullscreen; geolocation; microphone; camera; pointer-lock; clipboard-read; clipboard-write"
          style={{ 
            position: 'absolute', 
            height: '100%', 
            width: '100%', 
            border: 'none',
            pointerEvents: 'auto',
            touchAction: 'auto',
            userSelect: 'auto',
            zIndex: 1
          }}
        />
      )}
    </div>
  );
});

export default ScormCourseViewer;